import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import GroupIcon from '@mui/icons-material/Group'
import {
  Paper,
  Button,
  Grid,
  DialogContent,
  Typography,
  CircularProgress,
} from '@mui/material'
import {
  getReferralSource,
  getReferralSourceSpecialties,
  updateReferralSource,
} from 'src/services/AdminService'
import { Switch, FormControlLabel } from '@mui/material'
import { IReferralSourceDTO } from 'src/viewModels/referrals/ReferralSourceDTO'
import { Formik, Form, ErrorMessage } from 'formik'
import FullWidthField from 'src/common/FullWidthField'
import * as Yup from 'yup'
import States from 'src/utils/States'
import SelectInput from 'src/common/SelectInput/SelectInput'
import ReferralContactPaper from 'src/obmReferral/ReferralContacts'
import PhoneNumberMaskOBM from 'src/common/PhoneNumberMaskOBM'

const ReferralContact = () => {
  const [gridLoading, setGridLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [initialValues, setInitialValues] = useState<IReferralSourceDTO | null>(null)
  const [specialityOptions, setSpecialityOptions] = useState<string[]>([' '])

  const pathname = window.location.pathname
  const pathParts = pathname.split('/')
  const rsId = parseInt(pathParts[pathParts.length - 1], 10)
  const referralSourceId = !isNaN(rsId) ? rsId : null
  const states: string[] = States.map((state) => state.name)
  const dropdownState: string[] = [...[' '], ...states]

  const validationSchema = Yup.object().shape({
    sourceName: Yup.string().required('Referral source is required'),
    speciality: Yup.string(),
    billingAddress: Yup.string(),
    billingCity: Yup.string(),
    billingState: Yup.string(),
    billingZip: Yup.string(),
    billingPhone: Yup.string(),
    billingFax: Yup.string(),
    referralSourceEmail: Yup.string(),
    inActive: Yup.boolean(),
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setGridLoading(true)

        if (referralSourceId !== null) {
          const specialities = await getReferralSourceSpecialties(null, 1, 5000)

          setSpecialityOptions((prev) => {
            return [
              ...prev,
              ...(specialities.items
                ?.map((s) => s.speciality)
                .filter((item): item is string => item !== undefined) || []),
            ]
          })

          const data = await getReferralSource(referralSourceId)
          setInitialValues(data)
        }
      } finally {
        setGridLoading(false)
      }
    }

    fetchData()
  }, [referralSourceId])

  const handleSave = async (values: IReferralSourceDTO, shouldGoBack: boolean) => {
    try {
      setSaving(true)

      if (referralSourceId !== null) {
        const referralSourceData: IReferralSourceDTO = {
          ...values,
          rsId: referralSourceId,
        }
        await updateReferralSource(referralSourceId, referralSourceData)
        setInitialValues(values)
        if (shouldGoBack) {
          window.history.back()
        }
      }
    } finally {
      setSaving(false)
    }
  }

  const getValue = (item: string) => {
    return item
  }

  return (
    <>
      <Helmet>
        <title>Referral Source</title>
      </Helmet>
      <Formik
        initialValues={
          initialValues || {
            rsId: referralSourceId ?? 0,
            sourceName: '',
            speciality: '',
            billingAddress: '',
            billingCity: '',
            billingState: '',
            billingZip: '',
            billingPhone: '',
            billingFax: '',
            referralSourceEmail: '',
            inActive: false,
          }
        }
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => handleSave(values, false)}
      >
        {({ isValid, dirty, values, errors, touched, setFieldValue }) => (
          <Form>
            <ResponsiveAppBar
              title="Edit Referral Source and Contact Information"
              pageIcon={<GroupIcon />}
            >
              <Grid container direction="row" spacing={2} justifyContent="flex-end">
                <Grid item>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => window.history.back()}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="secondary"
                    size="small"
                    variant="contained"
                    onClick={() => handleSave(values, false)}
                    disabled={!dirty || !isValid || saving}
                  >
                    Save
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="secondary"
                    size="small"
                    variant="contained"
                    onClick={() => handleSave(values, true)}
                    disabled={!dirty || !isValid || saving}
                  >
                    Save and Close
                  </Button>
                </Grid>
              </Grid>
            </ResponsiveAppBar>

            {gridLoading && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '2',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}

            <Grid
              container
              direction="row"
              spacing={2}
              style={{ margin: '0', width: '100%' }}
            >
              <Grid item xs={12} md={6}>
                <Paper style={{ margin: '20px', height: '100%' }}>
                  <Typography
                    variant={'h6'}
                    color="textSecondary"
                    style={{
                      height: '18px',
                      paddingLeft: '20px',
                      fontSize: '18px',
                      paddingTop: '15px',
                      paddingBottom: '20px',
                    }}
                  >
                    REFERRAL DETAILS
                  </Typography>
                  <Grid container xs={12} direction="row">
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          required
                          variant="outlined"
                          autoFocus
                          margin="dense"
                          label="Referral Source"
                          name="sourceName"
                          fullWidth
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.sourceName && touched.sourceName}
                          helperText={<ErrorMessage name="sourceName" />}
                        />
                      </DialogContent>
                    </Grid>
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '18px 16px' }}>
                        <SelectInput
                          inputId="speciality"
                          getName={getValue}
                          getValue={getValue}
                          label="Referral Speciality"
                          name="speciality"
                          shrinkLabel={true}
                          fullWidth
                          outlined={true}
                          multiple={false}
                          native={true}
                          value={values.speciality}
                          items={specialityOptions}
                          onChange={(event) => {
                            setFieldValue('speciality', event.target.value)
                          }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>

                  <Grid container direction="row">
                    <Grid container item xs={6} direction="column">
                      <Typography
                        variant={'h6'}
                        color="textSecondary"
                        style={{
                          height: '18px',
                          paddingLeft: '20px',
                          fontSize: '18px',
                          paddingBottom: '20px',
                        }}
                      >
                        BILLING DETAILS
                      </Typography>
                      <Grid item>
                        <DialogContent style={{ padding: '8px 16px' }}>
                          <FullWidthField
                            variant="outlined"
                            margin="dense"
                            label="Billing Address"
                            name="billingAddress"
                            fullWidth
                            inputProps={{ maxLength: 50 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </DialogContent>
                      </Grid>
                      <Grid item>
                        <DialogContent style={{ padding: '8px 16px' }}>
                          <FullWidthField
                            variant="outlined"
                            margin="dense"
                            label="Billing City"
                            name="billingCity"
                            fullWidth
                            inputProps={{ maxLength: 30 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </DialogContent>
                      </Grid>
                      <Grid container item direction="row">
                        <Grid item xs={6}>
                          <DialogContent style={{ padding: '18px 16px' }}>
                            <SelectInput
                              inputId="billingState"
                              getName={getValue}
                              getValue={getValue}
                              label="State"
                              name="billingState"
                              fullWidth
                              shrinkLabel={true}
                              outlined={true}
                              multiple={false}
                              native={true}
                              value={values.billingState}
                              items={dropdownState}
                              onChange={(event) => {
                                setFieldValue('billingState', event.target.value)
                              }}
                            />
                          </DialogContent>
                        </Grid>
                        <Grid item xs={6}>
                          <DialogContent style={{ padding: '8px 16px' }}>
                            <FullWidthField
                              variant="outlined"
                              margin="dense"
                              label="Billing Zip"
                              name="billingZip"
                              fullWidth
                              inputProps={{ maxLength: 10 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </DialogContent>
                        </Grid>
                        <Grid item>
                          <DialogContent style={{ padding: '8px 16px' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  name="inActive"
                                  color="primary"
                                  checked={values.inActive}
                                  onChange={(event) => {
                                    setFieldValue('inActive', event.target.checked)
                                  }}
                                />
                              }
                              label={values.inActive ? 'Inactive' : 'Active'}
                              labelPlacement="end"
                            />
                          </DialogContent>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container item xs={6} direction="column">
                      <Typography
                        variant={'h6'}
                        color="textSecondary"
                        style={{
                          height: '18px',
                          paddingLeft: '20px',
                          fontSize: '18px',
                          paddingBottom: '20px',
                        }}
                      >
                        CONTACT DETAILS
                      </Typography>
                      <Grid item>
                        <DialogContent style={{ padding: '8px 16px' }}>
                          <FullWidthField
                            variant="outlined"
                            margin="dense"
                            label="Referral Source Email"
                            name="referralSourceEmail"
                            fullWidth
                            inputProps={{ maxLength: 50 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </DialogContent>
                      </Grid>
                      <Grid item>
                        <DialogContent style={{ padding: '8px 16px' }}>
                          <FullWidthField
                            variant="outlined"
                            margin="dense"
                            label="Billing Phone"
                            name="billingPhone"
                            fullWidth
                            InputProps={{
                              inputComponent: PhoneNumberMaskOBM,
                              maxLength: 10,
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </DialogContent>
                      </Grid>
                      <Grid item>
                        <DialogContent style={{ padding: '8px 16px' }}>
                          <FullWidthField
                            variant="outlined"
                            margin="dense"
                            label="Billing Fax"
                            name="billingFax"
                            fullWidth
                            InputProps={{
                              inputComponent: PhoneNumberMaskOBM,
                              maxLength: 10,
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </DialogContent>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} spacing={2}>
                <ReferralContactPaper></ReferralContactPaper>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default ReferralContact
